import React, { createContext, useContext, useReducer, useEffect } from 'react'
import { useAuth } from './AuthContext'
import apiService from '../services/api'

// Initial notification state
const initialState = {
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
  lastFetched: null,
}

// Notification actions
const NOTIFICATION_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_NOTIFICATIONS: 'SET_NOTIFICATIONS',
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  MARK_AS_READ: 'MARK_AS_READ',
  MARK_MULTIPLE_AS_READ: 'MARK_MULTIPLE_AS_READ',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
}

// Notification reducer
const notificationReducer = (state, action) => {
  switch (action.type) {
    case NOTIFICATION_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      }

    case NOTIFICATION_ACTIONS.SET_NOTIFICATIONS:
      return {
        ...state,
        notifications: action.payload,
        unreadCount: action.payload.filter(n => !n.is_read).length,
        isLoading: false,
        error: null,
        lastFetched: new Date().toISOString(),
      }

    case NOTIFICATION_ACTIONS.ADD_NOTIFICATION:
      const newNotification = action.payload
      const updatedNotifications = [newNotification, ...state.notifications]
      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount: updatedNotifications.filter(n => !n.is_read).length,
      }

    case NOTIFICATION_ACTIONS.MARK_AS_READ:
      const updatedNotificationsRead = state.notifications.map(n =>
        n.id === action.payload ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
      )
      return {
        ...state,
        notifications: updatedNotificationsRead,
        unreadCount: updatedNotificationsRead.filter(n => !n.is_read).length,
      }

    case NOTIFICATION_ACTIONS.MARK_MULTIPLE_AS_READ:
      const notificationIds = action.payload
      const updatedMultipleRead = state.notifications.map(n =>
        notificationIds.includes(n.id) 
          ? { ...n, is_read: true, read_at: new Date().toISOString() } 
          : n
      )
      return {
        ...state,
        notifications: updatedMultipleRead,
        unreadCount: updatedMultipleRead.filter(n => !n.is_read).length,
      }

    case NOTIFICATION_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      }

    case NOTIFICATION_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      }

    default:
      return state
  }
}

// Create Notification Context
const NotificationContext = createContext(null)

// Notification Provider Component
export const NotificationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(notificationReducer, initialState)
  const { isAuthenticated, canManageConnections } = useAuth()

  // Fetch notifications
  const fetchNotifications = async (isRead = null) => {
    if (!isAuthenticated || !canManageConnections) return

    dispatch({ type: NOTIFICATION_ACTIONS.SET_LOADING, payload: true })

    try {
      const params = {}
      if (isRead !== null) {
        params.is_read = isRead
      }

      const response = await apiService.get('/notifications/', params)

      if (response.success) {
        dispatch({
          type: NOTIFICATION_ACTIONS.SET_NOTIFICATIONS,
          payload: response.data.data || []
        })
      } else {
        dispatch({
          type: NOTIFICATION_ACTIONS.SET_ERROR,
          payload: response.error?.message || 'Failed to fetch notifications'
        })
      }
    } catch (error) {
      dispatch({
        type: NOTIFICATION_ACTIONS.SET_ERROR,
        payload: error.message || 'Failed to fetch notifications'
      })
    }
  }

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      const response = await apiService.patch('/notifications/', {
        notification_ids: [notificationId]
      })

      if (response.success) {
        dispatch({
          type: NOTIFICATION_ACTIONS.MARK_AS_READ,
          payload: notificationId
        })
        return true
      } else {
        console.error('Failed to mark notification as read:', response.error)
        return false
      }
    } catch (error) {
      console.error('Error marking notification as read:', error)
      return false
    }
  }

  // Mark multiple notifications as read
  const markMultipleAsRead = async (notificationIds) => {
    if (!notificationIds || notificationIds.length === 0) return false

    try {
      const response = await apiService.patch('/notifications/', {
        notification_ids: notificationIds
      })

      if (response.success) {
        dispatch({
          type: NOTIFICATION_ACTIONS.MARK_MULTIPLE_AS_READ,
          payload: notificationIds
        })
        return true
      } else {
        console.error('Failed to mark notifications as read:', response.error)
        return false
      }
    } catch (error) {
      console.error('Error marking notifications as read:', error)
      return false
    }
  }

  // Mark all as read
  const markAllAsRead = async () => {
    const unreadIds = state.notifications
      .filter(n => !n.is_read)
      .map(n => n.id)

    if (unreadIds.length === 0) return true

    return await markMultipleAsRead(unreadIds)
  }

  // Get unread notifications
  const getUnreadNotifications = () => {
    return state.notifications.filter(n => !n.is_read)
  }

  // Get notifications by type
  const getNotificationsByType = (type) => {
    return state.notifications.filter(n => n.notification_type === type)
  }

  // Add new notification (for real-time updates)
  const addNotification = (notification) => {
    dispatch({
      type: NOTIFICATION_ACTIONS.ADD_NOTIFICATION,
      payload: notification
    })
  }

  // Clear error
  const clearError = () => {
    dispatch({ type: NOTIFICATION_ACTIONS.CLEAR_ERROR })
  }

  // Auto-fetch notifications when user is authenticated and can manage connections
  useEffect(() => {
    if (isAuthenticated && canManageConnections) {
      fetchNotifications()
      
      // Set up periodic refresh (every 30 seconds)
      const interval = setInterval(() => {
        fetchNotifications()
      }, 30000)

      return () => clearInterval(interval)
    }
  }, [isAuthenticated, canManageConnections])

  // Context value
  const contextValue = {
    // State
    ...state,
    
    // Methods
    fetchNotifications,
    markAsRead,
    markMultipleAsRead,
    markAllAsRead,
    addNotification,
    clearError,
    
    // Utilities
    getUnreadNotifications,
    getNotificationsByType,
    hasUnreadNotifications: state.unreadCount > 0,
    
    // Notification types for easy reference
    TYPES: {
      CONNECTION_REQUEST: 'connection_request',
      CONNECTION_ACCEPTED: 'connection_accepted',
      CONNECTION_REJECTED: 'connection_rejected',
      ROLE_UPDATED: 'role_updated',
    }
  }

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  )
}

// Custom hook to use notification context
export const useNotifications = () => {
  const context = useContext(NotificationContext)
  
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  
  return context
}

// Export notification actions for external use
export { NOTIFICATION_ACTIONS }
